<template>
    <article>
        <p>
            While working on <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent</a>,
            we set out to measure network latency between containers and the services they communicate with.

            Since the agent has already discovered the endpoints that each container communicates with,
            we just need to measure network latency.
            We embedded "pinger" directly into the agent to measure end-to-end latency
            because the ICMP Echo requests should be sent from within the network namespace of each container.
        </p>

        <p>
            I've looked at the most popular pinger implementations on Go, and unfortunately, all of them use userspace-generated timestamps to
            calculate RTTs (Round-Trip Times). This can lead to significant measurement errors, especially in the case of a lack of CPU time
            on the node due to throttling or high utilization.
        </p>

        <p>
            Let's look at how ping actually measures RTT.
        </p>

        <v-img max-width="80%" class="ma-auto mb-6" src="/static/img/blog/ping_rtt.svg" />

        <p>
            Though it looks simple enough, the devil is in the details. First, let's see how a packet is sent:
        </p>

        <v-img max-width="80%" class="ma-auto mb-6" src="/static/img/blog/ping_sending.svg" />

        <p>
            The main problem here is that a considerable amount of time can elapse between the request for a current timestamp and the actual sending of a packet.
            We can also see the same error in the receiving stage:
        </p>

        <v-img max-width="80%" class="ma-auto mb-6" src="/static/img/blog/ping_receiving.svg" />

        <p>
            To illustrate the case where the error is quite noticeable, let's run ping in a container that is limited in CPU time.
            The <var>-U</var> flag tells ping to use userspace-generated timestamps.
        </p>


        <pre v-hl.sh>
docker run --rm -ti --cpu-period=10000 --cpu-quota=1000 --name pinger ping 8.8.8.8 -U
        </pre>

        <p>
            Then I'll start a CPU-consuming app in the same container:
        </p>

        <pre v-hl.sh>
docker exec -ti pinger stress --cpu 4
        </pre>

        <p>Here is the output:</p>

        <pre v-hl>
PING 8.8.8.8 (8.8.8.8) 56(84) bytes of data.
64 bytes from 8.8.8.8: icmp_seq=1 ttl=114 time=1.21 ms
64 bytes from 8.8.8.8: icmp_seq=2 ttl=114 time=1.29 ms
64 bytes from 8.8.8.8: icmp_seq=3 ttl=114 time=1.01 ms
64 bytes from 8.8.8.8: icmp_seq=4 ttl=114 time=1.37 ms
64 bytes from 8.8.8.8: icmp_seq=5 ttl=114 time=1.31 ms
64 bytes from 8.8.8.8: icmp_seq=6 ttl=114 time=1.59 ms
64 bytes from 8.8.8.8: icmp_seq=7 ttl=114 time=1.75 ms
64 bytes from 8.8.8.8: icmp_seq=8 ttl=114 time=1.09 ms
64 bytes from 8.8.8.8: icmp_seq=9 ttl=114 time=1.33 ms
64 bytes from 8.8.8.8: icmp_seq=10 ttl=114 time=1.14 ms
64 bytes from 8.8.8.8: icmp_seq=11 ttl=114 time=<span class="marked">30.0 ms</span> &lt;- stress started
64 bytes from 8.8.8.8: icmp_seq=12 ttl=114 time=<span class="marked">20.0 ms</span>
64 bytes from 8.8.8.8: icmp_seq=13 ttl=114 time=<span class="marked">20.0 ms</span>
64 bytes from 8.8.8.8: icmp_seq=14 ttl=114 time=<span class="marked">20.0 ms</span>
64 bytes from 8.8.8.8: icmp_seq=15 ttl=114 time=<span class="marked">9.99 ms</span>
64 bytes from 8.8.8.8: icmp_seq=16 ttl=114 time=<span class="marked">30.0 ms</span>
64 bytes from 8.8.8.8: icmp_seq=17 ttl=114 time=<span class="marked">40.0 ms</span>
64 bytes from 8.8.8.8: icmp_seq=18 ttl=114 time=<span class="marked">20.0 ms</span>
        </pre>



        <p>
            As you can see, the measurement results were severely affected.
            If we run ping without <var>-U</var>, <var>stress</var> does not affect RTT at all.
            How does ping generate timestamps to exclude extra time spent before the packet is actually sent or after it is received?
        </p>

        <p>
            There is an interface called <a href="https://www.kernel.org/doc/Documentation/networking/timestamping.txt" rel="noopener noreferrer">SO_TIMESTAMPING</a> that
            allows a userspace program to request the transmission/reception timestamp of a particular packet from the kernel.
            <var>SO_TIMESTAMPING</var> supports multiple timestamp sources, but we will only use those that do not require special hardware or device driver support.
        </p>

        <ul>
            <li>
                <var>SOF_TIMESTAMPING_TX_SCHED</var>:
                Request tx timestamps prior to entering the packet scheduler.
            </li>
            <li>
                <var>SOF_TIMESTAMPING_RX_SOFTWARE</var>:
                These timestamps are generated just after a device driver hands a packet to the kernel receive stack.
            </li>
        </ul>

        <p>
            <var>SO_TIMESTAMPING</var> can be enabled for a particular packet
            (by using <a href="https://man7.org/linux/man-pages/man3/cmsg.3.html" target="_blank" rel="noopener noreferrer">control messages</a>) or for every packet passing through the socket:
        </p>

        <pre v-hl.go>
flags := unix.SOF_TIMESTAMPING_SOFTWARE | unix.SOF_TIMESTAMPING_RX_SOFTWARE | unix.SOF_TIMESTAMPING_TX_SCHED |
         unix.SOF_TIMESTAMPING_OPT_CMSG | unix.SOF_TIMESTAMPING_OPT_TSONLY

err := syscall.SetsockoptInt(socketFd, unix.SOL_SOCKET, unix.SO_TIMESTAMPING, flags)
        </pre>

        <p>
            Immediately after we have sent a packet to the socket,
            we can request the kernel for the timestamp of the packet transmission:
        </p>

        <pre v-hl.go>
err := send(sock, pkt.seq, ip)
// ...
oob := make([]byte, 1024) // a buffer for Out-Of-Band data where the kernel will write the timestamp
// MSG_ERRQUEUE indicates that we want to receive a message from the socket's error queue
_, oobn, _, _, err := syscall.Recvmsg(socketFd, pktBuf, oob, syscall.MSG_ERRQUEUE)
sentAt, err := getTimestampFromOutOfBandData(oob, oobn)
        </pre>

        <p>
            Then parsing the received message to extract the timestamp:
        </p>

        <pre v-hl.go>
func getTimestampFromOutOfBandData(oob []byte, oobn int) (time.Time, error) {
	cms, err := syscall.ParseSocketControlMessage(oob[:oobn])
	if err != nil {
		return time.Time{}, err
	}
	for _, cm := range cms {
		if cm.Header.Level == syscall.SOL_SOCKET || cm.Header.Type == syscall.SO_TIMESTAMPING {
			var t unix.ScmTimestamping
			if err := binary.Read(bytes.NewBuffer(cm.Data), binary.LittleEndian, &t); err != nil {
				return time.Time{}, err
			}
			return time.Unix(t.Ts[0].Unix()), nil
		}
	}
	return time.Time{}, fmt.Errorf("no timestamp found")
}
        </pre>

        <p>Upon receiving, ancillary data is available along with the packet data, so there is no need to read anything from the socket error queue:</p>

        <pre v-hl.go>
n, oobn, _, ra, err := conn.ReadMsgIP(pktBuf, oob)
//...
receivedAt, err := getTimestampFromOutOfBandData(oob, oobn)
        </pre>

<p>And finally, we can calculate the RTT:</p>

        <pre v-hl.go>
rtt := receivedAt.Sub(sentAt)
        </pre>

        <p>
            Another thing worth noting is that the timestamp of when the packet has been sent must be stored in the app's memory, not in the packet's payload.
            Otherwise, it can lead to <a href="https://twitter.com/m_ou_se/status/1480184732562374656" target="_blank" rel="noopener noreferrer">funny consequences</a>.
        </p>

        <p>
            The resulting round-trip time reflects the real network latency much more accurately and
            is not subject to the mentioned errors. The agent uses this method to collect the <MetricLink name="container_net_latency_seconds" exporter="node-agent"/> metric for each container.
            Since this metric is broken down by <var>destination_ip</var>,
            you can always find out what the network latency between the container and each one of the services it communicates with is.
            The limitation of this method is that it doesn't work if ICMP traffic is blocked, such as in the case of Amazon RDS instances.
        </p>

        <h2>Useful links</h2>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
        </ul>
    </article>
</template>

<script>

import MetricLink from "@/components/MetricLink";

export default {
    components: {MetricLink}
}
</script>

<style scoped>
.marked {
    color: yellow;
}
</style>
